import React, { useMemo, useRef, useState } from "react";
import {
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import Swal from "sweetalert2";
import PrestamosParametrizadosService from "../../services/prestamosParametrizadosService";
import { getApiError } from "./utils";
import {
  MAX_CLIENTES,
  descargarPlantillaClientes,
  leerClientesDeExcel,
  separarValores,
} from "./excelClientes";

const inp =
  "w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-custom-green-ccard-1 transition-colors";

/**
 * Lista de clientes de un préstamo parametrizado por clientes.
 *
 * Se cargan de a uno (o varios pegados) por DNI o ID de cliente, o subiendo un Excel.
 * Cada carga se valida contra la API (Oracle CLIENTE) antes de agregarse.
 * Lo que se sube se SUMA a la lista (los repetidos no se duplican).
 *
 * Props:
 *   clientes  — [{ idCliente, documento, nombre, deBaja }]
 *   onChange  — (clientes) => void
 */
export default function ClientesSelector({ clientes, onChange }) {
  const [tipoManual, setTipoManual] = useState("DOCUMENTO");
  const [texto, setTexto] = useState("");
  const [validando, setValidando] = useState(false);
  const [noEncontrados, setNoEncontrados] = useState([]);
  const [filtro, setFiltro] = useState("");
  const fileRef = useRef(null);

  /** Valida contra la API y agrega los encontrados. */
  const agregar = async (tipo, valores, origen) => {
    if (valores.length === 0) return;
    setValidando(true);
    try {
      const res = await PrestamosParametrizadosService.validarClientes(tipo, valores);
      const data = res?.data?.data ?? {};
      const encontrados = data.encontrados ?? [];
      const faltan = data.noEncontrados ?? [];

      const yaEstan = new Set(clientes.map((c) => c.idCliente));
      const nuevos = encontrados.filter((c) => !yaEstan.has(c.idCliente));
      const total = clientes.length + nuevos.length;
      if (total > MAX_CLIENTES) {
        Swal.fire(
          "Demasiados clientes",
          `El préstamo quedaría con ${total} clientes y el máximo es ${MAX_CLIENTES}.`,
          "warning",
        );
        return;
      }

      onChange([...clientes, ...nuevos]);
      setNoEncontrados(faltan);

      const repetidos = encontrados.length - nuevos.length;
      const deBaja = nuevos.filter((c) => c.deBaja).length;
      Swal.fire({
        icon: faltan.length ? "warning" : "success",
        title: `${origen}: ${nuevos.length} cliente(s) agregado(s)`,
        html: [
          repetidos ? `${repetidos} ya estaban en la lista.` : "",
          faltan.length ? `<b>${faltan.length} no se encontraron</b> (se listan debajo de la tabla).` : "",
          deBaja ? `${deBaja} tienen la cuenta dada de baja: no van a ver el préstamo hasta que estén activos.` : "",
        ]
          .filter(Boolean)
          .join("<br/>"),
        confirmButtonColor: "#56C401",
      });
    } catch (err) {
      Swal.fire("Error", getApiError(err, "No se pudieron validar los clientes.").message, "error");
    } finally {
      setValidando(false);
    }
  };

  const handleAgregarManual = async () => {
    const valores = separarValores(texto);
    if (valores.length === 0) return;
    await agregar(tipoManual, valores, "Carga manual");
    setTexto("");
  };

  const handleExcel = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // permite volver a subir el mismo archivo
    if (!file) return;
    try {
      const { tipo, valores } = await leerClientesDeExcel(file);
      await agregar(tipo, valores, `Excel (${tipo === "DOCUMENTO" ? "por DNI" : "por ID de cliente"})`);
    } catch (err) {
      Swal.fire("No se pudo leer el Excel", err.message, "error");
    }
  };

  const quitar = (idCliente) => onChange(clientes.filter((c) => c.idCliente !== idCliente));

  const quitarTodos = async () => {
    const { isConfirmed } = await Swal.fire({
      title: "¿Quitar todos los clientes?",
      text: `Se quitan los ${clientes.length} clientes de la lista (se aplica al guardar).`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Quitar todos",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#e53e3e",
      reverseButtons: true,
    });
    if (isConfirmed) {
      onChange([]);
      setNoEncontrados([]);
    }
  };

  const visibles = useMemo(() => {
    const f = filtro.trim().toLowerCase();
    if (!f) return clientes;
    return clientes.filter(
      (c) =>
        String(c.documento ?? "").includes(f) ||
        String(c.idCliente).includes(f) ||
        String(c.nombre ?? "").toLowerCase().includes(f),
    );
  }, [clientes, filtro]);

  return (
    <div className="space-y-3 rounded-xl border border-gray-100 bg-gray-50 p-3">
      {/* Carga manual */}
      <div>
        <div className="mb-1 flex items-center gap-3 text-xs text-gray-500">
          <span className="font-medium">Agregar por:</span>
          {[
            ["DOCUMENTO", "DNI"],
            ["ID_CLIENTE", "ID de cliente"],
          ].map(([valor, label]) => (
            <label key={valor} className="flex items-center gap-1 cursor-pointer">
              <input
                type="radio"
                name="tipoManual"
                className="accent-green-600"
                checked={tipoManual === valor}
                onChange={() => setTipoManual(valor)}
              />
              {label}
            </label>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            className={inp}
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleAgregarManual();
              }
            }}
            placeholder={
              tipoManual === "DOCUMENTO"
                ? "Ej: 30111222 — podés pegar varios separados por coma o espacio"
                : "Ej: 154321 — podés pegar varios separados por coma o espacio"
            }
          />
          <button
            type="button"
            onClick={handleAgregarManual}
            disabled={validando || !texto.trim()}
            className="whitespace-nowrap rounded-xl bg-custom-green-ccard-1 px-4 py-2 text-sm font-medium text-white hover:bg-custom-green-ccard-2 disabled:opacity-50"
          >
            {validando ? "Validando…" : "Agregar"}
          </button>
        </div>
      </div>

      {/* Excel */}
      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={fileRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          className="hidden"
          onChange={handleExcel}
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={validando}
          className="flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50"
        >
          <ArrowUpTrayIcon className="h-4 w-4" />
          Subir Excel
        </button>
        <button
          type="button"
          onClick={descargarPlantillaClientes}
          className="flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-medium text-custom-green-ccard-1 hover:bg-green-50"
        >
          <ArrowDownTrayIcon className="h-4 w-4" />
          Descargar plantilla
        </button>
        <span className="text-xs text-gray-400">
          Columna <b>DNI</b> o <b>ID_CLIENTE</b>, un cliente por fila. Se suma a la lista.
        </span>
      </div>

      {/* Lista */}
      <div className="rounded-xl border border-gray-200 bg-white">
        <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-3 py-2">
          <span className="text-xs font-medium text-gray-600">
            {clientes.length} cliente{clientes.length !== 1 ? "s" : ""} asignado
            {clientes.length !== 1 ? "s" : ""}
          </span>
          <div className="flex items-center gap-2">
            {clientes.length > 10 && (
              <input
                className="w-40 rounded-lg border border-gray-200 px-2 py-1 text-xs"
                placeholder="Buscar DNI, ID o nombre"
                value={filtro}
                onChange={(e) => setFiltro(e.target.value)}
              />
            )}
            {clientes.length > 0 && (
              <button
                type="button"
                onClick={quitarTodos}
                className="text-xs text-red-500 hover:underline"
              >
                Quitar todos
              </button>
            )}
          </div>
        </div>

        {clientes.length === 0 ? (
          <p className="px-3 py-4 text-center text-xs text-gray-400">
            Todavía no hay clientes. Agregalos de a uno o subí un Excel.
          </p>
        ) : (
          <div className="max-h-48 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-gray-50 text-gray-500">
                <tr>
                  <th className="px-3 py-1.5 text-left font-medium">DNI</th>
                  <th className="px-3 py-1.5 text-left font-medium">Cliente</th>
                  <th className="px-3 py-1.5 text-left font-medium">ID</th>
                  <th />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {visibles.slice(0, 500).map((c) => (
                  <tr key={c.idCliente}>
                    <td className="px-3 py-1.5 text-gray-700">{c.documento ?? "—"}</td>
                    <td className="px-3 py-1.5 text-gray-600">
                      {c.nombre ?? "—"}
                      {c.deBaja && (
                        <span className="ml-1 rounded bg-red-50 px-1 text-[10px] text-red-600">de baja</span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-gray-400">{c.idCliente}</td>
                    <td className="px-2 py-1 text-right">
                      <button
                        type="button"
                        onClick={() => quitar(c.idCliente)}
                        title="Quitar"
                        className="rounded p-1 text-red-400 hover:bg-red-50"
                      >
                        <TrashIcon className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {visibles.length > 500 && (
              <p className="px-3 py-1.5 text-center text-[11px] text-gray-400">
                Se muestran 500 de {visibles.length}. Usá el buscador para encontrar uno.
              </p>
            )}
          </div>
        )}
      </div>

      {/* No encontrados de la última carga */}
      {noEncontrados.length > 0 && (
        <div className="rounded-xl border border-yellow-200 bg-yellow-50 px-3 py-2 text-xs text-yellow-800">
          <div className="flex items-start justify-between gap-2">
            <span>
              <b>{noEncontrados.length} no se encontraron</b> en la última carga y no se agregaron:{" "}
              {noEncontrados.slice(0, 30).join(", ")}
              {noEncontrados.length > 30 ? "…" : ""}
            </span>
            <button type="button" onClick={() => setNoEncontrados([])} title="Cerrar">
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
